/* ─────────────────────────  THE DEVICE'S LOCATION  ───────────────────────────
 *
 * ONE module owns it (BUILDING_TIME_PLAN.md R-T10, Plan C WP-C11): whether the
 * app may ask, the asking, the one retry, and what to say when the answer is
 * no. The building forms and the signup city field are the only two callers,
 * and nothing else in the app may call navigator.geolocation.
 *
 * THE RULES IT KEEPS
 *   * Our own ask comes first — one sentence saying what the location is for,
 *     with "Not now" (components/location-ask). The BROWSER's prompt follows a
 *     tap, never a page load: a prompt nobody asked for is refused, and Safari
 *     then stays refused.
 *   * A refusal is never a dead end. Every screen works without a location,
 *     and says how to turn it back on — in words for the platform in front of
 *     the person, and without pointing at an address bar an installed app does
 *     not have.
 *   * The position never SETS anything. It centres a map or offers a city; the
 *     person still places the pin or picks the city (R-T2).
 *   * It is not stored, and it leaves the browser only rounded to two decimals
 *     (about a kilometre) — see roundCoordinate.
 */

export type LocationPermission =
  /** Already allowed: no ask is shown; the location is simply used. */
  | 'granted'
  /** Not decided yet: show our ask. */
  | 'prompt'
  /** Refused: say so, and say how to turn it back on. */
  | 'denied'
  /** No Geolocation API at all (or an insecure page): say nothing, offer nothing. */
  | 'unsupported'
  /** The Permissions API cannot say (older Safari): treat as `prompt`. */
  | 'unknown';

export type LocationFailure = 'denied' | 'unavailable' | 'unsupported';

export type LocationOutcome =
  | { ok: true; latitude: number; longitude: number }
  | { ok: false; reason: LocationFailure };

const hasGeolocation = (): boolean =>
  typeof navigator !== 'undefined' && Boolean(navigator.geolocation);

/**
 * Whether the app may ask. Never throws and never prompts: on a browser whose
 * Permissions API is missing, or rejects 'geolocation' (older Safari), the
 * answer is `unknown` and the caller shows the ask.
 */
export const locationPermission = async (): Promise<LocationPermission> => {
  if (!hasGeolocation()) return 'unsupported';

  const permissions = (
    navigator as Navigator & {
      permissions?: { query: (d: { name: string }) => Promise<{ state: string }> };
    }
  ).permissions;
  if (!permissions || typeof permissions.query !== 'function') return 'unknown';

  try {
    const result = await permissions.query({ name: 'geolocation' });
    if (result.state === 'granted') return 'granted';
    if (result.state === 'denied') return 'denied';
    if (result.state === 'prompt') return 'prompt';

    return 'unknown';
  } catch {
    return 'unknown';
  }
};

/**
 * ASK THE DEVICE WHERE IT IS. Call this only from a tap, or when
 * locationPermission() has already answered `granted`.
 *
 * ONE RETRY, for anything but a refusal. A first position request fails for
 * ordinary reasons — a location service that is still starting, a desktop with
 * no fix cached — and succeeds a moment later (seen in Chromium, 3 runs in 10:
 * an error while permission was `granted`, then the same call answering).
 * Telling the person "your location could not be found" for that would send
 * them away from a button that works. A refusal is final and is never retried.
 */
export const requestDeviceLocation = (): Promise<LocationOutcome> =>
  new Promise<LocationOutcome>(resolve => {
    if (!hasGeolocation()) {
      resolve({ ok: false, reason: 'unsupported' });

      return;
    }

    const ask = (attempt: number): void => {
      navigator.geolocation.getCurrentPosition(
        position =>
          resolve({
            ok: true,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          }),
        error => {
          const refused = error.code === error.PERMISSION_DENIED;
          if (!refused && attempt === 1) {
            ask(2);

            return;
          }
          resolve({ ok: false, reason: refused ? 'denied' : 'unavailable' });
        },
        { timeout: attempt === 1 ? 8000 : 15000, maximumAge: 10 * 60 * 1000 }
      );
    };

    ask(1);
  });

/**
 * A coordinate as it may leave the browser for a "what city is near me"
 * lookup: two decimals, about a kilometre. The exact position is never sent to
 * our server or to the address provider, and never reaches an access log.
 */
export const roundCoordinate = (value: number): number => Math.round(value * 100) / 100;

/* ───────────────────────────────  "Not now"  ─────────────────────────────── */

const DECLINED_KEY = 'lobbero.location-ask.declined-at';
const DECLINED_FOR_MS = 30 * 24 * 60 * 60 * 1000;

/** Did somebody on this device answer "Not now" in the last 30 days? Storage
 *  can throw (a private window, a locked-down profile): then nobody did. */
export const declinedRecently = (now: number = Date.now()): boolean => {
  try {
    const at = Number(window.localStorage.getItem(DECLINED_KEY) || 0);

    return at > 0 && now - at < DECLINED_FOR_MS;
  } catch {
    return false;
  }
};

export const rememberDeclined = (now: number = Date.now()): void => {
  try {
    window.localStorage.setItem(DECLINED_KEY, String(now));
  } catch {
    // Forgetting the answer means asking again next time, which is harmless.
  }
};

export const forgetDeclined = (): void => {
  try {
    window.localStorage.removeItem(DECLINED_KEY);
  } catch {
    // As above.
  }
};

/* ────────────────────────  Where the app is running  ─────────────────────── */

/**
 * Running as an installed app (added to the home screen) rather than in a
 * browser tab. There is then no address bar — which decides the words below,
 * and whether the install banner shows at all (install-prompt.tsx).
 */
export const isStandalone = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }
  } catch {
    // matchMedia is missing in some test environments.
  }

  // iOS Safari
  return (window.navigator as unknown as { standalone?: boolean }).standalone === true;
};

export type DevicePlatform = 'ios' | 'android' | 'desktop';

export const devicePlatform = (): DevicePlatform => {
  if (typeof navigator === 'undefined') return 'desktop';
  const agent = navigator.userAgent || '';
  // An iPad on iPadOS reports itself as a Mac; the touch points give it away.
  const touchMac = /Macintosh/i.test(agent) && (navigator.maxTouchPoints || 0) > 1;
  if (/iphone|ipad|ipod/i.test(agent) || touchMac) return 'ios';
  if (/android/i.test(agent)) return 'android';

  return 'desktop';
};

/**
 * HOW TO TURN LOCATION BACK ON, in words for the device in front of the person.
 *
 * An installed app has no address bar, so its wording never points at one. An
 * iPhone's permission lives in Settings whichever way the app is opened.
 */
export const turnLocationBackOnHelp = (
  platform: DevicePlatform = devicePlatform(),
  standalone: boolean = isStandalone()
): string => {
  if (platform === 'ios') {
    return standalone
      ? 'To turn it back on: open Settings, go to Privacy & Security, then Location Services, find Lobbero in the list and choose "While Using the App".'
      : 'To turn it back on: open Settings, go to Privacy & Security, then Location Services, then Safari Websites, and choose "While Using the App". Then reload this page.';
  }
  if (platform === 'android') {
    return standalone
      ? 'To turn it back on: press and hold the Lobbero icon, open App info, then Permissions, then Location, and choose "Allow only while using the app".'
      : 'To turn it back on: tap the icon beside the web address, open Permissions or Site settings, and allow Location. Then reload this page.';
  }

  return standalone
    ? 'To turn it back on: open the app menu (the three dots at the top of the window), choose App info or Site settings, and allow Location.'
    : 'To turn it back on: click the icon beside the web address, open Site settings, and allow Location. Then reload this page.';
};
